import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Autocomplete, AutocompleteProps, Box, BoxProps, TextFieldProps } from '@mui/material';
import CustomTextField from '../Inputs/CustomTextField';
import { ReactComponent as ArrowDownIcon } from '../../assets/arrow-triangle-down.svg';
import LoaderComponent from 'components/Loader';
import { debounce } from '@mui/material';

import { useCacheDataController } from 'Context/contexts/CacheDataContext';

interface Option {
    value: string;
    label: string;
}

interface CustomAutocompleteProps extends Omit<AutocompleteProps<any, any, any, any>, 'renderInput'> {
    textFieldLabel?: string | JSX.Element;
    customTextFieldProps?: TextFieldProps;
    onChange?:
        | ((
            event: React.SyntheticEvent,
            value: any,
          ) => void)
        | undefined;
    onInputChange?: (
        event: React.SyntheticEvent,
        value: string,
        reason: any
    ) => void;
    renderInput?: (params: any) => React.ReactNode;
    options: Option[];
    fieldValueOptionsCallback?: (input: string) => Promise<Option[]>;
    columnName: string;
    value?: any;
    boxProps?: BoxProps;
	inputSx?: any;
    disabled?: boolean;
}

const useDebouncedHandleBlur = (
    callback: (
        defOpt?: Option[],
        callbackFn?: React.Dispatch<React.SetStateAction<Option[]>>,
        loadingCallback?: React.Dispatch<React.SetStateAction<boolean>>
    ) => void,
    delay: number
) => {

const timeoutRef = useRef<number | null>(null);
const debouncedHandleBlur = (
    defOpt?: Option[],
    callbackFn?: React.Dispatch<React.SetStateAction<Option[]>>,
    loadingCallback?: React.Dispatch<React.SetStateAction<boolean>>
    ) => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }
        timeoutRef.current = window.setTimeout(() => {
            callback(defOpt, callbackFn, loadingCallback);
        }, delay);
    };

    return debouncedHandleBlur;
};

const CustomAutoCompleteIndex: React.FC<CustomAutocompleteProps> = ({
    textFieldLabel,
    customTextFieldProps,
    renderInput,
    onChange,
    options = [],
    fieldValueOptionsCallback,
    columnName,
    inputValue,
    value,
    boxProps,
    inputSx,
    onInputChange,
    disabled,
    ...rest
}) => {
    const [autoInputValue, setInputValue] = useState(inputValue ?? '');
    const [searchTerm, setSearchTerm] = useState('');
    const [defaultOptions, setDefaultOptions] = useState<Option[]>(options);
    const [filteredItems, setFilteredItems] = useState<Option[]>(options);
    const [loading, setLoading] = useState(false);
    const { sx, ...props } = boxProps || {};

    const [dispatch] = useCacheDataController();

    const truncatedLabel = value  ? (value.value.length > 25 ? value.value.substring(0, 25) + '...' : value.value || '') : '';

    // Sync defaultOptions with options prop when it changes
    useEffect(() => {
        setDefaultOptions(options);
    }, [options]);

    const isFetchOptionEnabled = !!fieldValueOptionsCallback && searchTerm.length > 2;

    const toggleDropdown = useDebouncedHandleBlur(
        (
            defOpt?: Option[],
            callbackFn?: React.Dispatch<React.SetStateAction<Option[]>>,
            loadingCallback?: React.Dispatch<React.SetStateAction<boolean>>
        ) => {
            if (defOpt?.length === 0) {
                loadingCallback?.(true);
                fetchOptions('')
                    .then((opts) => callbackFn?.(opts))
                    .finally(() => loadingCallback?.(false));
            }
        },
        10
    );

    const fetchOptions = async (searchInput: string): Promise<Option[]> => {
        try {
            if (fieldValueOptionsCallback) {
                const optionArray = await fieldValueOptionsCallback(searchInput);
                return optionArray;
            }
            return [];
        } catch (error) {
            console.error('Error fetching options:', error);
            return [];
        }
    };

    useEffect(() => {
        if (!isFetchOptionEnabled && defaultOptions && defaultOptions.length > 0) {
            setFilteredItems(defaultOptions);
        }
    }, [defaultOptions]);

    const debouncedFetchOption = useCallback(
        debounce((newValue) => {
            if (!!fieldValueOptionsCallback && newValue.length > 2) {
                setLoading(true);
                fetchOptions(newValue)
                    .then((option) => {
                        setFilteredItems(option);
                    })
                    .finally(() => setLoading(false));
            } else {

                setFilteredItems(defaultOptions.filter((item) => typeof item.value === 'string' && item.value.toLowerCase().includes(newValue.toLowerCase())));
            }
        }, 300),
        [ defaultOptions, dispatch]
    );

    const handleInputChange = (e: any, newInputValue: string, reason: string) => {
        setInputValue(newInputValue);
        if(reason === 'input') {
            setSearchTerm(newInputValue);
            debouncedFetchOption(newInputValue);
            onInputChange?.(e, newInputValue, reason);
        }
    };

    return (
        <Box
            sx={{
                backgroundColor: 'blue.dark',
                borderRadius: '4px',
                fontSize: '15px',
                zIndex: 999,
                minWidth: '90px',
                textAlign: 'left',
                height: 'fit-content',
                width: truncatedLabel ? `${truncatedLabel.length * 8 + 70}px` : '90px',
                ...sx,
            }}
            {...props}
        >
        <Autocomplete
            options={(filteredItems?.length ?? 0) > 0 ? filteredItems : defaultOptions || []}
            onFocus={() => toggleDropdown(defaultOptions, setDefaultOptions, setLoading)}
            getOptionLabel={(option) => (typeof option === 'object' ? option.displayLabel : option)}
            renderOption={(props, option) => (
                <li {...props} key={option.value}>{option.label}</li>
            )}
            inputValue={autoInputValue}
            value={value}
            isOptionEqualToValue={(option, value) => option.value === value.value}
            onChange={(e, selectedOption) => onChange?.(e, selectedOption)}
            onInputChange={(e, newInput, reason) => {handleInputChange(e, newInput, reason)}}
            disabled={disabled}
            {...rest}
            sx={inputSx}
            popupIcon={<ArrowDownIcon />}
            renderInput={
                renderInput ||
                ((params) => (
                    <CustomTextField
                        {...params}
                        label={textFieldLabel}
                        {...customTextFieldProps}
                        InputProps={{ ...params.InputProps, ...customTextFieldProps?.InputProps }}
                    />
                ))
            }
            noOptionsText={
                loading ? <LoaderComponent size={20} /> : 'No program found'
              }
        />
        </Box>
    );
};

export default CustomAutoCompleteIndex;
